/**
 * DataProvider abstraction for the layout rendering engine.
 *
 * Each host app (Electron, Web) implements its own DataProvider hook that
 * collects data from stores and sockets and maps it to InjectedData.
 * The renderer and editor consume data through this context, so they never
 * depend on any app-specific store or socket.
 */

import React, { createContext, useContext } from "react";
import type { InjectedData } from "./layout-types";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export type LayoutFontFormat = "woff2" | "ttf" | "otf";

export interface LayoutFontFaceItem {
  family: string;
  fontUrl: string;
  format: LayoutFontFormat;
}

export interface LayoutFontFaceListResult {
  apiUrl: string;
  fonts: LayoutFontFaceItem[];
  token?: string;
}

export type GetLayoutFontFaces = () => Promise<LayoutFontFaceListResult | null>;

interface LayoutDataContextValue {
  data: InjectedData;
  getLayoutFontFaces?: GetLayoutFontFaces;
}

const DataProviderCtx = createContext<LayoutDataContextValue | null>(null);

/**
 * Consumes the injected layout data from the nearest DataProvider.
 *
 * @returns The current InjectedData, or an empty object when no provider exists.
 */
export function useLayoutData(): InjectedData {
  return useContext(DataProviderCtx)?.data ?? {};
}

/**
 * Returns the optional font-face provider exposed by the host app.
 *
 * @returns A callback that can list user font faces, or undefined.
 */
export function useLayoutFontFacesProvider(): GetLayoutFontFaces | undefined {
  return useContext(DataProviderCtx)?.getLayoutFontFaces;
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

interface LayoutDataProviderProps {
  data: InjectedData;
  getLayoutFontFaces?: GetLayoutFontFaces;
  children: React.ReactNode;
}

/**
 * Wraps children with the InjectedData context so that the renderer and
 * editor can access live competition data (or mock data in editor mode).
 *
 * @example
 * // In Electron:
 * const data = useElectronLayoutData();
 * <LayoutDataProvider data={data}><LayoutRenderer ... /></LayoutDataProvider>
 *
 * // In Web:
 * const data = useWebLayoutData(meetId, token);
 * <LayoutDataProvider data={data}><LayoutRenderer ... /></LayoutDataProvider>
 */
export const LayoutDataProvider: React.FC<LayoutDataProviderProps> = ({
  data,
  getLayoutFontFaces,
  children,
}) => {
  return (
    <DataProviderCtx.Provider value={{ data, getLayoutFontFaces }}>
      {children}
    </DataProviderCtx.Provider>
  );
};
